import i18n from "i18next";
import { WebStorageNames } from "./constants";
import { WebStorage } from "./webStorage";

export const USER_LANGUAGE_STORAGE_KEY = "userLanguage";

/** Supported app language codes (i18next resource keys). */
export const APP_LANGUAGE_CODES = [
  "en",
  "es",
  "ja",
  "tr",
  "ar",
  "zh_cn",
  "rus",
  "de",
  "fr",
  "nl",
  "hi",
  "ur",
  "ko",
  "kk",
  "az",
  "uz",
  "it",
  "sv",
  "pt_br",
  "en_za",
  "bn",
];

/** ISO 3166-1 alpha-2 codes for Semantic UI country flags (reliable on Windows). */
export const LANGUAGE_COUNTRY_CODES = {
  en: "us",
  es: "es",
  ja: "jp",
  tr: "tr",
  ar: "sa",
  zh_cn: "cn",
  rus: "ru",
  de: "de",
  fr: "fr",
  nl: "nl",
  hi: "in",
  ur: "pk",
  ko: "kr",
  kk: "kz",
  az: "az",
  uz: "uz",
  it: "it",
  sv: "se",
  pt_br: "br",
  en_za: "za",
  bn: "bd",
};

/**
 * Each language written in itself. Used on logged-out pages so a visitor can always
 * find their language, even when the page is currently shown in one they cannot read.
 */
export const LANGUAGE_NATIVE_NAMES = {
  en: "English",
  es: "Español",
  ja: "日本語",
  tr: "Türkçe",
  ar: "العربية",
  zh_cn: "简体中文",
  rus: "Русский",
  de: "Deutsch",
  fr: "Français",
  nl: "Nederlands",
  hi: "हिन्दी",
  ur: "اردو",
  ko: "한국어",
  kk: "Қазақша",
  az: "Azərbaycanca",
  uz: "Oʻzbekcha",
  it: "Italiano",
  sv: "Svenska",
  pt_br: "Português (Brasil)",
  en_za: "English (South Africa)",
  bn: "বাংলা",
};

export const DEFAULT_PRIMARY_LANGUAGE = "en";

/** Map stored / API language values to i18next resource keys (underscore form). */
export function normalizeAppLanguageKey(raw) {
  if (!raw) return DEFAULT_PRIMARY_LANGUAGE;
  const s = String(raw).trim().toLowerCase().replace(/-/g, "_");
  if (s === "zh" || s === "zh_cn") return "zh_cn";
  if (s === "pt" || s === "pt_br") return "pt_br";
  if (s === "ru" || s === "rus") return "rus";
  if (APP_LANGUAGE_CODES.includes(s)) return s;
  return DEFAULT_PRIMARY_LANGUAGE;
}

/** Semantic UI flag country code (e.g. "gb", "es"). */
export function getLanguageCountryCode(code) {
  const key = normalizeAppLanguageKey(code);
  return LANGUAGE_COUNTRY_CODES[key] || LANGUAGE_COUNTRY_CODES[DEFAULT_PRIMARY_LANGUAGE];
}

/** @deprecated Use getLanguageCountryCode with LanguageFlag component */
export function getLanguageFlag(code) {
  return getLanguageCountryCode(code);
}

export function getStoredUserLanguage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  const stored = window.localStorage.getItem(USER_LANGUAGE_STORAGE_KEY);
  return stored ? normalizeAppLanguageKey(stored) : null;
}

/**
 * Apply language to i18n and persist for the current user session.
 * Pass { persistUserInfo: false } on logged-out pages so no userInfo record is created.
 */
export function applyAppLanguage(code, { persistUserInfo = true } = {}) {
  const normalized = normalizeAppLanguageKey(code);
  if (typeof window !== "undefined") {
    if (window.localStorage) {
      window.localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, normalized);
    }
    if (document.documentElement) {
      document.documentElement.lang = normalized.replace(/_/g, "-");
    }
  }
  if (persistUserInfo) {
    persistPrimaryLanguageInUserInfo(normalized);
  }

  if (i18n.language === normalized) {
    return Promise.resolve(normalized);
  }
  return i18n.changeLanguage(normalized).then(() => normalized);
}

/** Read primary language from API payloads (login, register, GetUserDetails, etc.). */
export function getPrimaryLanguageFromPayload(data) {
  if (!data || typeof data !== "object") return null;
  const raw =
    data.primaryLanguage ??
    data.PrimaryLanguage ??
    data.language ??
    data.Language;
  return raw ? normalizeAppLanguageKey(raw) : null;
}

/** @deprecated Use getPrimaryLanguageFromPayload */
export const getLanguageFromAuthPayload = getPrimaryLanguageFromPayload;

export function getPrimaryLanguageFromUserInfo(userInfo) {
  if (!userInfo || typeof userInfo !== "object") return null;
  const raw =
    userInfo.primaryLanguage ??
    userInfo.PrimaryLanguage ??
    userInfo.language ??
    userInfo.Language;
  return raw ? normalizeAppLanguageKey(raw) : null;
}

/** @deprecated Use getPrimaryLanguageFromUserInfo */
export const getLanguageFromUserInfo = getPrimaryLanguageFromUserInfo;

/** Merge auth/user-data payload into stored userInfo (defaults to English). */
export function withPrimaryLanguageFromAuth(userInfo, authPayload) {
  const lang =
    getPrimaryLanguageFromPayload(authPayload) || DEFAULT_PRIMARY_LANGUAGE;
  return { ...userInfo, primaryLanguage: lang };
}

export function persistPrimaryLanguageInUserInfo(primaryLanguage) {
  if (typeof window === "undefined") return;
  const normalized = normalizeAppLanguageKey(primaryLanguage);
  try {
    const existing = WebStorage.getLocalStore(WebStorageNames.UserInfo) || {};
    WebStorage.setLocalStore(WebStorageNames.UserInfo, {
      ...existing,
      primaryLanguage: normalized,
    });
  } catch {
    /* ignore corrupt userInfo */
  }
}

/** @deprecated Use persistPrimaryLanguageInUserInfo */
export const persistLanguageInUserInfo = persistPrimaryLanguageInUserInfo;

/** Update stored userInfo when userdata is loaded (e.g. Account/GetUserDetails). */
export function syncStoredUserInfoFromUserData(userData) {
  const lang = getPrimaryLanguageFromPayload(userData);
  if (!lang) return null;
  applyAppLanguage(lang);
  const existing = WebStorage.getLocalStore(WebStorageNames.UserInfo) || {};
  WebStorage.setLocalStore(WebStorageNames.UserInfo, {
    ...existing,
    primaryLanguage: lang,
  });
  return lang;
}
